import { morph } from '@theme/morph';
import { updateCartBubbles } from './quick-add-cart.js';

/** @type {WeakMap<HTMLSelectElement, { originalVariantId: number }>} */
const pendingBySelect = new WeakMap();

/** @type {Set<HTMLSelectElement>} */
const applying = new Set();

function getSectionIds() {
  return [
    ...new Set(
      [...document.querySelectorAll('cart-items-component[data-section-id]')]
        .map((c) => c.dataset.sectionId)
        .filter(Boolean)
    ),
  ];
}

/**
 * @param {number} cents
 */
function formatCents(cents) {
  if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function') {
    return Shopify.formatMoney(cents);
  }
  return `$${(cents / 100).toFixed(2)}`;
}

/**
 * @param {HTMLElement | null | undefined} wrapper
 * @param {{ resetSelect?: boolean }} [options]
 */
function closeVariantPicker(wrapper, { resetSelect = false } = {}) {
  if (!(wrapper instanceof HTMLElement)) return;

  const select = wrapper.querySelector('.cart-variant-dropdown');
  const trigger = wrapper.querySelector('.cart-item__change-variant-trigger');
  const picker = wrapper.querySelector('.cart-item__variant-picker');

  wrapper.classList.remove('is-open', 'has-pending', 'is-updating');

  if (select instanceof HTMLSelectElement) {
    const pending = pendingBySelect.get(select);
    if (resetSelect && pending) select.value = String(pending.originalVariantId);
    pendingBySelect.delete(select);
  }

  picker?.classList.remove('is-visible');
  if (picker instanceof HTMLElement) picker.hidden = true;

  if (trigger instanceof HTMLElement) {
    trigger.hidden = false;
    trigger.setAttribute('aria-expanded', 'false');
  }
}

/**
 * Refresh only the checkout footer (totals, discount, checkout button).
 * Never touches the product list rows — slots stay fixed.
 * @param {string[]} sectionIds
 */
async function refreshSummaryOnly(sectionIds) {
  if (!sectionIds.length) return;
  const url = `${window.location.pathname}?sections=${sectionIds.join(',')}&_=${Date.now()}`;
  const sections = await fetch(url).then((r) => r.json());

  for (const sectionId of sectionIds) {
    const html = sections[sectionId];
    if (!html) continue;
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const newSummary = doc.querySelector('.cart-drawer__summary');
    const curSummary = document.querySelector('.cart-drawer__summary');
    if (newSummary instanceof HTMLElement && curSummary instanceof HTMLElement) {
      morph(curSummary, newSummary);
    }
  }
}

/**
 * @param {Record<string, string>} sections
 * @param {string[]} sectionIds
 * @param {string} itemKey
 */
function getPriceRowHTMLFromSections(sections, sectionIds, itemKey) {
  if (!sections || !itemKey) return null;

  const escapedKey =
    typeof CSS !== 'undefined' && typeof CSS.escape === 'function'
      ? CSS.escape(itemKey)
      : itemKey.replace(/\\/g, '\\\\').replace(/"/g, '\\"');

  for (const sectionId of sectionIds) {
    const sectionHtml = sections[sectionId];
    if (!sectionHtml) continue;

    const doc = new DOMParser().parseFromString(sectionHtml, 'text/html');
    const sectionRow = doc.querySelector(`.cart-items__table-row[data-key="${escapedKey}"]`);
    const sectionPriceRow = sectionRow?.querySelector('.cart-items__price-row');

    if (sectionPriceRow instanceof HTMLElement && sectionPriceRow.innerHTML.trim()) {
      return sectionPriceRow.innerHTML;
    }
  }

  return null;
}

/**
 * @param {HTMLSelectElement} select
 * @param {number} variantId
 */
function getVariantCompareAtPrice(select, variantId) {
  const option = select.querySelector(`option[value="${String(variantId)}"]`);
  if (!(option instanceof HTMLOptionElement)) return 0;

  const raw = option.getAttribute('data-compare-at-price');
  if (raw !== null && raw !== '') return Number(raw) || 0;

  return Number(option.dataset.compareAtPrice) || 0;
}

/**
 * @param {number} variantId
 */
async function fetchVariantCompareAtPrice(variantId) {
  try {
    const response = await fetch(`/variants/${variantId}.js`);
    if (!response.ok) return 0;
    const variant = await response.json();
    return Number(variant.compare_at_price) || 0;
  } catch {
    return 0;
  }
}

/**
 * @param {HTMLSelectElement} select
 * @param {number} variantId
 */
async function resolveCompareAtPrice(select, variantId) {
  const fromOption = getVariantCompareAtPrice(select, variantId);
  if (fromOption > 0) return fromOption;
  return fetchVariantCompareAtPrice(variantId);
}

/**
 * Build price row HTML matching cart-products.liquid markup.
 * @param {Record<string, unknown>} item
 * @param {number} compareAtPrice
 */
function buildPriceRowHTML(item, compareAtPrice) {
  const final = Number(item.final_price);
  const original = Number(item.original_price);
  const compare = Number(compareAtPrice) || 0;
  const onSale = original !== final;

  let html = '';

  if (onSale) {
    html += '<span class="visually-hidden">Sale price</span>';
    html += `<span class="cart-items__sale-price">${formatCents(final)}</span>`;
    html += '<span class="visually-hidden">Regular price</span>';
    html += `<s class="compare-at-price">${formatCents(compare > original ? compare : original)}</s>`;
    html += '<span class="compare-at-label">Comp. value</span>';
    return html;
  }

  if (compare > original) {
    html += '<span class="visually-hidden">Sale price</span>';
  } else {
    html += '<span class="visually-hidden">Price</span>';
  }

  const priceClass = compare > original ? 'cart-items__sale-price' : 'cart-items__price-current';
  html += `<span class="${priceClass}">${formatCents(original)}</span>`;

  if (compare > original) {
    html += '<span class="visually-hidden">Regular price</span>';
    html += `<s class="compare-at-price">${formatCents(compare)}</s>`;
    html += '<span class="compare-at-label">Comp. value</span>';
  }

  return html;
}

/**
 * Update price, variant text, image and select in the existing DOM row.
 * Nothing else in the cart list is touched.
 * @param {HTMLElement} row
 * @param {Record<string, unknown>} item  — one item from /cart.js items[]
 * @param {number} newVariantId
 * @param {HTMLSelectElement} select
 * @param {{ priceRowHTML?: string | null, compareAtPrice?: number }} [options]
 */
function patchRowInPlace(row, item, newVariantId, select, options = {}) {
  /* Price row — prefer server-rendered markup so styling always matches Liquid */
  const priceRow = row.querySelector('.cart-items__price-row');
  if (priceRow instanceof HTMLElement) {
    if (options.priceRowHTML) {
      priceRow.innerHTML = options.priceRowHTML;
    } else {
      const compareAtPrice =
        typeof options.compareAtPrice === 'number'
          ? options.compareAtPrice
          : getVariantCompareAtPrice(select, newVariantId);
      priceRow.innerHTML = buildPriceRowHTML(item, compareAtPrice);
    }
  }

  /* Variant text */
  const dl = row.querySelector('.cart-items__variants');
  if (dl instanceof HTMLElement && Array.isArray(item.options_with_values)) {
    const nodes = dl.querySelectorAll('.cart-items__variant');
    item.options_with_values.forEach((opt, i) => {
      const dd = nodes[i]?.querySelector('dd');
      if (dd) dd.textContent = String(opt.value);
    });
  }

  /* Image */
  const img = row.querySelector('.cart-items__media-image');
  if (img instanceof HTMLImageElement && typeof item.image === 'string' && item.image) {
    img.src = item.image;
  }

  /* Update select state so next change() sees the right currentVariantId */
  select.dataset.currentVariantId = String(newVariantId);
  select.value = String(newVariantId);

  /* Update row's cart key so remove / qty-change hits the right line */
  row.dataset.key = String(item.key);

  closeVariantPicker(select.closest('.cart-item__variant-change'));
}

/**
 * @param {HTMLElement} trigger
 */
function openVariantPicker(trigger) {
  const wrapper = trigger.closest('.cart-item__variant-change');
  if (!(wrapper instanceof HTMLElement) || wrapper.classList.contains('is-open')) return;

  /* Close any other open pickers */
  document.querySelectorAll('.cart-item__variant-change.is-open').forEach((openWrapper) => {
    if (openWrapper === wrapper) return;
    const openSelect = openWrapper.querySelector('.cart-variant-dropdown');
    if (openSelect instanceof HTMLSelectElement && openWrapper.classList.contains('has-pending')) {
      applyVariantChange(openSelect);
    } else {
      closeVariantPicker(openWrapper);
    }
  });

  wrapper.classList.add('is-open');
  trigger.setAttribute('aria-expanded', 'true');
  trigger.hidden = true;

  const picker = wrapper.querySelector('.cart-item__variant-picker');
  if (picker instanceof HTMLElement) {
    picker.hidden = false;
    requestAnimationFrame(() => picker.classList.add('is-visible'));
  }
}

/**
 * Core update: 2 API calls only (change.js + add.js), then patch the row.
 * @param {HTMLSelectElement} select
 */
async function applyVariantChange(select) {
  if (!(select instanceof HTMLSelectElement) || applying.has(select)) return;

  const wrapper = select.closest('.cart-item__variant-change');
  if (!(wrapper instanceof HTMLElement)) return;

  const newVariantId    = Number(select.value);
  const currentVariantId = Number(select.dataset.currentVariantId);

  if (!newVariantId || newVariantId === currentVariantId) {
    closeVariantPicker(wrapper);
    return;
  }

  applying.add(select);

  const row      = select.closest('.cart-items__table-row');
  const oldKey   = row instanceof HTMLElement ? row.dataset.key   : '';
  const quantity = row instanceof HTMLElement ? Number(row.dataset.quantity) || 1 : 1;
  const sectionIds = getSectionIds();

  wrapper.classList.add('is-updating');
  row?.classList.add('cart-item-row--updating');
  row?.classList.remove('cart-item-row--pending');

  try {
    if (!oldKey || !row) throw new Error('Missing cart line metadata');

    /* ── Step 1: remove old variant line ── */
    await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: oldKey, quantity: 0 }),
    });

    /* ── Step 2: add new variant (request sections for accurate price markup) ── */
    const addResponse = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        id: newVariantId,
        quantity,
        sections: sectionIds.join(','),
        sections_url: window.location.pathname,
      }),
    });
    const addResult = await addResponse.json();
    if (!addResponse.ok) {
      throw new Error(addResult.description || addResult.message || 'Add to cart failed');
    }

    /* ── Step 3: get updated cart to read new item data ── */
    const updatedCart = await fetch(`/cart.js?_=${Date.now()}`).then((r) => r.json());

    /* Find the new item by variant id (prefer most recently added line) */
    const matchingItems = updatedCart.items.filter((i) => i.variant_id === newVariantId);
    const updatedItem = matchingItems[matchingItems.length - 1];
    if (!updatedItem) throw new Error('New variant not found in cart');

    const priceRowHTML = getPriceRowHTMLFromSections(addResult.sections, sectionIds, updatedItem.key);
    const compareAtPrice = priceRowHTML ? 0 : await resolveCompareAtPrice(select, newVariantId);

    /* ── Step 4: patch this row only — list order never changes ── */
    patchRowInPlace(row, updatedItem, newVariantId, select, { priceRowHTML, compareAtPrice });

    /* ── Step 5: refresh footer totals only (async, non-blocking for UX) ── */
    updateCartBubbles(updatedCart.item_count);
    refreshSummaryOnly(sectionIds); /* fire and forget */

  } catch (error) {
    console.error('Variant update error:', error);
    closeVariantPicker(wrapper, { resetSelect: true });
  } finally {
    applying.delete(select);
    if (row?.isConnected) row.classList.remove('cart-item-row--updating', 'cart-item-row--pending');
    if (wrapper.isConnected) wrapper.classList.remove('is-updating', 'has-pending');
  }
}

/* ── Event listeners ── */

document.addEventListener('click', (event) => {
  const trigger = event.target instanceof Element
    ? event.target.closest('.cart-item__change-variant-trigger')
    : null;
  if (!(trigger instanceof HTMLElement)) return;
  event.preventDefault();
  openVariantPicker(trigger);
});

document.addEventListener('change', (event) => {
  const select = event.target;
  if (!(select instanceof HTMLSelectElement) || !select.classList.contains('cart-variant-dropdown')) return;

  const wrapper = select.closest('.cart-item__variant-change');
  if (!(wrapper instanceof HTMLElement)) return;

  const currentVariantId = Number(select.dataset.currentVariantId);
  const newVariantId     = Number(select.value);

  if (newVariantId && newVariantId !== currentVariantId) {
    wrapper.classList.add('has-pending');
    pendingBySelect.set(select, { originalVariantId: currentVariantId });
    wrapper.closest('.cart-items__table-row')?.classList.add('cart-item-row--pending');
  } else {
    wrapper.classList.remove('has-pending');
    pendingBySelect.delete(select);
    wrapper.closest('.cart-items__table-row')?.classList.remove('cart-item-row--pending');
  }
});

document.addEventListener('pointerdown', (event) => {
  const target = event.target;
  if (!(target instanceof Element)) return;

  document.querySelectorAll('.cart-item__variant-change.is-open').forEach((wrapper) => {
    if (!(wrapper instanceof HTMLElement) || wrapper.contains(target)) return;
    const select = wrapper.querySelector('.cart-variant-dropdown');
    if (!(select instanceof HTMLSelectElement)) return;

    if (wrapper.classList.contains('has-pending')) {
      applyVariantChange(select);
    } else {
      closeVariantPicker(wrapper);
    }
  });
}, true);

document.addEventListener('focusout', (event) => {
  const select = event.target;
  if (!(select instanceof HTMLSelectElement) || !select.classList.contains('cart-variant-dropdown')) return;

  const wrapper = select.closest('.cart-item__variant-change');
  if (!(wrapper instanceof HTMLElement) || !wrapper.classList.contains('is-open')) return;

  window.setTimeout(() => {
    if (!wrapper.isConnected || !wrapper.classList.contains('is-open')) return;
    if (document.activeElement === select || applying.has(select)) return;

    if (wrapper.classList.contains('has-pending')) {
      applyVariantChange(select);
    } else {
      closeVariantPicker(wrapper);
    }
  }, 50);
});
